/**
 * Card Configuration Service - API client for dashboard customization
 *
 * Provides functions to interact with the /api/cards endpoints:
 * - Configure card with natural language (Haiku)
 * - Update card configuration
 * - Update dashboard layout
 * - Reset dashboard customizations
 * - Get customized dashboard
 */

import axios from 'axios';
import config from '../config/config.js';

const API_BASE_URL = config.apiBaseUrl;

/**
 * Configure a card using natural language with Claude Haiku
 *
 * @param {string} prompt - Natural language description (e.g., "Show revenue trend for last year")
 * @param {string} dashboardId - Dashboard ID
 * @param {string} cardId - Card ID (optional, for editing existing card)
 * @param {Object} currentConfig - Current card configuration (optional)
 * @returns {Promise<Object>} Response with recommendation and chart definition
 */
export const configureCardWithAI = async (prompt, dashboardId, cardId = null, currentConfig = null) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/cards/configure`, {
      prompt,
      dashboard_id: dashboardId,
      card_id: cardId,
      current_config: currentConfig
    });
    return response.data;
  } catch (error) {
    console.error('Error configuring card with AI:', error);
    throw error;
  }
};

/**
 * Update a card's configuration (saves to Snowflake)
 *
 * @param {string} dashboardId - Dashboard ID
 * @param {string} cardId - Card ID (e.g., 'section_one')
 * @param {Object} chartDefinition - Full chart definition to save
 * @returns {Promise<Object>} Response with success status and customization ID
 */
export const updateCardConfig = async (dashboardId, cardId, chartDefinition) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/api/cards/${dashboardId}/${cardId}`,
      { chart_definition: chartDefinition }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating card configuration:', error);
    throw error;
  }
};

/**
 * Update dashboard layout (card order) - saves to Snowflake
 *
 * @param {string} dashboardId - Dashboard ID
 * @param {Array<string>} cardOrder - Ordered list of card IDs
 * @param {string} templateId - Template ID for validation
 * @param {string} userId - User ID (default: 'global')
 * @returns {Promise<Object>} Response with success status and customization ID
 */
export const updateCardLayout = async (dashboardId, cardOrder, templateId, userId = 'global') => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/cards/dashboard/${dashboardId}/layout`,
      {
        card_order: cardOrder,
        template_id: templateId,
        user_id: userId
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating card layout:', error);
    throw error;
  }
};

/**
 * Reset dashboard to default configuration
 *
 * Deletes customizations from Snowflake.
 *
 * @param {string} dashboardId - Dashboard ID
 * @param {string} resetType - Type of reset: 'all', 'layout', or 'cards'
 * @param {string} userId - User ID (default: 'global')
 * @returns {Promise<Object>} Response with deletion count
 */
export const resetDashboardCustomizations = async (dashboardId, resetType = 'all', userId = 'global') => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/api/cards/dashboard/${dashboardId}/customizations`,
      {
        data: {
          reset_type: resetType,
          user_id: userId
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error resetting dashboard:', error);
    throw error;
  }
};

/**
 * Get dashboard configuration merged with user customizations
 *
 * Returns the merged configuration (base YAML + Snowflake customizations).
 *
 * @param {string} dashboardId - Dashboard ID
 * @param {string} userId - User ID (default: 'global')
 * @returns {Promise<Object>} Merged dashboard configuration
 */
export const getCustomizedDashboard = async (dashboardId, userId = 'global') => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/cards/dashboard/${dashboardId}/customized`,
      {
        params: { user_id: userId }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching customized dashboard:', error);
    throw error;
  }
};

export default {
  configureCardWithAI,
  updateCardConfig,
  updateCardLayout,
  resetDashboardCustomizations,
  getCustomizedDashboard
};
